// ============================================================================
// Authenticated Add Feed resource bridge
// ============================================================================
// /addfeed is a login-required surface, while its presenter intentionally
// remains in src/webforms without AuthContext coupling. This is the one narrow
// app-side seam between those constraints: it owns authenticated QueryScope
// through useRouteResource and injects plain lookup props into the form.
//
// Do not move this into src/webforms. A public webform module must never be
// able to choose a scope for authenticated farm tables. The outer gate also
// means a logged-out state unmounts the resource consumer before it can build
// a public query key, rather than merely disabling a public-scope request.
// ============================================================================
import React from 'react';

import {useAuth} from '../../contexts/AuthContext.jsx';
import {RESOURCE} from './routeDataCatalog.js';
import type {AddFeedConfigData, AddFeedInputsData} from './routeDataCatalog.js';
import {useRouteResource} from './useRouteResource.js';
import type {AddFeedResourceProps} from '../../webforms/AddFeedWebform.js';
import type {SessionSubmitter} from '../../webforms/webformsRoutes.js';

type AddFeedPresenterProps = AddFeedResourceProps & {
  sessionSubmitter?: SessionSubmitter;
};

type AddFeedAuthenticatedBridgeProps = {
  children: React.ReactElement<AddFeedPresenterProps>;
};

type AuthenticatedAddFeedResourcesProps = AddFeedAuthenticatedBridgeProps & {
  sessionSubmitter: SessionSubmitter;
};

const ADD_FEED_RESOURCE_PARAMS = Object.freeze({});

function AuthenticatedAddFeedResources({children, sessionSubmitter}: AuthenticatedAddFeedResourcesProps) {
  // The two former effects were independent: master feed inputs may arrive
  // before (or after) the six-row config bundle. Neither resource blocks the
  // other and both failures degrade to the same empty/default state the form
  // previously rendered; no route-level error screen is introduced here.
  // Do not surface either resource's loading/error projection here. The old
  // effects rendered their initial values while pending and after a rejected
  // request, so data-only projection is the compatible contract.
  const {data: inputsData} = useRouteResource(RESOURCE.WEBFORM_ADD_FEED_INPUTS, ADD_FEED_RESOURCE_PARAMS);
  const {data: configData} = useRouteResource(RESOURCE.WEBFORM_ADD_FEED_CONFIG, ADD_FEED_RESOURCE_PARAMS);
  const feedInputs = ((inputsData as AddFeedInputsData | null)?.feedInputs || []) as AddFeedResourceProps['feedInputs'];
  const addFeedConfig = (configData as AddFeedConfigData | null) || null;

  return React.cloneElement(children, {feedInputs, addFeedConfig, sessionSubmitter});
}

export default function AddFeedAuthenticatedBridge({children}: AddFeedAuthenticatedBridgeProps) {
  const {authState} = useAuth();

  // App's LoginScreen gate normally makes this branch unreachable. Keep the
  // local fail-closed guard anyway: a future pre-gate mount must not turn these
  // authenticated table resources into public-scope queries.
  if (authState === null || authState === false) return null;

  // Add Feed's LockedSubmitter controls and its RPC attribution are keyed by
  // this exact display-name fallback. Keep the identity with the authenticated
  // bridge alongside the data resources rather than accepting an App-owned
  // prop through the route entry; the presenter remains a plain, auth-free
  // component.
  const sessionSubmitter: SessionSubmitter = {
    name: authState.name || authState.user?.email || '',
  };

  return <AuthenticatedAddFeedResources sessionSubmitter={sessionSubmitter}>{children}</AuthenticatedAddFeedResources>;
}
